/**
 * Token Dependency Resolution System
 *
 * This module provides comprehensive token dependency resolution including:
 * - Token reference tracking and resolution
 * - Circular dependency detection
 * - Token value resolution with proper error handling
 * - Transformation pipeline for token processing
 */

import {
  DesignToken,
  TokenGroup,
  TokenSet,
  TokenReference,
  TokenDependencyGraph,
  TokenTransform,
  TransformContext,
  TokenValue,
  ValidationResult,
  ValidationError,
  ValidationWarning,
} from "../types/designTokens";

/**
 * Token resolution options
 */
export interface TokenResolutionOptions {
  /** Maximum depth for reference resolution */
  maxDepth?: number;
  /** Whether to throw on circular dependencies */
  throwOnCircular?: boolean;
  /** Whether to resolve nested references */
  resolveNested?: boolean;
  /** Custom transforms to apply during resolution */
  transforms?: TokenTransform[];
}

/**
 * Token resolution result
 */
export interface TokenResolutionResult {
  /** Resolved token set */
  tokenSet: TokenSet;
  /** Dependency graph */
  dependencyGraph: TokenDependencyGraph;
  /** Resolution validation */
  validation: ValidationResult;
  /** Resolution statistics */
  statistics: {
    totalTokens: number;
    resolvedReferences: number;
    circularDependencies: number;
    maxDepth: number;
  };
}

/**
 * Token Dependency Resolver
 *
 * Main class for resolving token dependencies and detecting circular references
 */
export class TokenDependencyResolver {
  private maxDepth: number;
  private throwOnCircular: boolean;
  private resolveNested: boolean;
  private transforms: TokenTransform[];

  constructor(options: TokenResolutionOptions = {}) {
    this.maxDepth = options.maxDepth ?? 50;
    this.throwOnCircular = options.throwOnCircular ?? false;
    this.resolveNested = options.resolveNested ?? true;
    this.transforms = options.transforms ?? [];
  }

  /**
   * Resolve all token references in a token set
   */
  async resolveTokenSet(
    tokenSet: TokenSet,
    options: TokenResolutionOptions = {},
  ): Promise<TokenResolutionResult> {
    const resolver = new TokenDependencyResolver({
      maxDepth: options.maxDepth ?? this.maxDepth,
      throwOnCircular: options.throwOnCircular ?? this.throwOnCircular,
      resolveNested: options.resolveNested ?? this.resolveNested,
      transforms: options.transforms ?? this.transforms,
    });

    // Build dependency graph
    const dependencyGraph = resolver.buildDependencyGraph(tokenSet);

    // Detect circular dependencies
    const circularDependencies =
      resolver.detectCircularDependencies(dependencyGraph);

    // Validate dependencies
    const validation = resolver.validateDependencies(
      dependencyGraph,
      circularDependencies,
    );

    // Resolve token values
    const resolvedTokenSet = resolver.resolveTokenValues(
      tokenSet,
      dependencyGraph,
    );

    // Apply transforms
    const transformedTokenSet = resolver.applyTransforms(resolvedTokenSet);

    // Calculate statistics
    const statistics = resolver.calculateStatistics(
      dependencyGraph,
      circularDependencies,
    );

    return {
      tokenSet: transformedTokenSet,
      dependencyGraph,
      validation,
      statistics,
    };
  }

  /**
   * Build dependency graph for token set
   */
  buildDependencyGraph(tokenSet: TokenSet): TokenDependencyGraph {
    const nodes = new Map<string, TokenReference>();
    const edges = new Map<string, Set<string>>();
    const circular = new Set<string>();

    const processToken = (
      token: DesignToken | TokenGroup | Record<string, any>,
      path: string,
      visited: Set<string> = new Set(),
    ): void => {
      if (visited.has(path)) {
        circular.add(path);
        return;
      }

      visited.add(path);

      // Check if it's a token group (has children or no $type)
      if (this.isTokenGroup(token)) {
        const tokenGroup = token as TokenGroup;
        if (tokenGroup.children) {
          Object.entries(tokenGroup.children).forEach(([key, value]) => {
            const childPath = path ? `${path}.${key}` : key;
            processToken(
              value as DesignToken | TokenGroup,
              childPath,
              new Set(visited),
            );
          });
        }
      } else if (token && typeof token === "object" && "$type" in token) {
        // It's a design token
        const designToken = token as DesignToken;
        const tokenRef: TokenReference = {
          path,
          resolved: false,
        };

        // Check if token value is a reference
        if (
          typeof designToken.$value === "string" &&
          this.isTokenReference(designToken.$value)
        ) {
          const refPath = this.extractReferencePath(designToken.$value);
          tokenRef.value = designToken.$value; // Keep original reference

          // Add edge from this token to referenced token
          // Convert reference to full path
          const fullRefPath = refPath.startsWith("tokens.")
            ? refPath
            : `tokens.${refPath}`;
          if (!edges.has(path)) {
            edges.set(path, new Set());
          }
          edges.get(path)!.add(fullRefPath);
        } else {
          tokenRef.value = designToken.$value;
          tokenRef.resolved = true;
        }

        nodes.set(path, tokenRef);
      }
    };

    const processTokens = (
      tokens: Record<string, any>,
      basePath: string,
    ): void => {
      Object.entries(tokens).forEach(([key, value]) => {
        const tokenPath = `${basePath}.${key}`;
        processToken(value, tokenPath, new Set<string>());
      });
    };

    if (tokenSet.tokens) {
      processTokens(tokenSet.tokens, "tokens");
    }

    // Detect circular dependencies
    const detectedCircular = this.detectCircularDependencies({
      nodes,
      edges,
      circular: new Set(),
    });
    detectedCircular.forEach((node) => circular.add(node));

    return {
      nodes,
      edges,
      circular,
    };
  }

  /**
   * Detect circular dependencies using DFS
   */
  detectCircularDependencies(
    dependencyGraph: TokenDependencyGraph,
  ): Set<string> {
    const circular = new Set<string>();
    const visiting = new Set<string>();
    const visited = new Set<string>();

    const dfs = (nodePath: string, path: string[]): boolean => {
      if (circular.has(nodePath)) {
        return true;
      }

      if (visiting.has(nodePath)) {
        // Found a cycle
        const cycleStart = path.indexOf(nodePath);
        if (cycleStart !== -1) {
          const cycle = path.slice(cycleStart);
          cycle.forEach((node) => circular.add(node));
        }
        return true;
      }

      if (visited.has(nodePath)) {
        return false;
      }

      visiting.add(nodePath);
      const currentPath = [...path, nodePath];

      const dependencies = dependencyGraph.edges.get(nodePath);
      if (dependencies) {
        for (const dep of dependencies) {
          if (dfs(dep, currentPath)) {
            return true;
          }
        }
      }

      visiting.delete(nodePath);
      visited.add(nodePath);
      return false;
    };

    // Check all nodes
    for (const nodePath of dependencyGraph.nodes.keys()) {
      if (!visited.has(nodePath)) {
        dfs(nodePath, []);
      }
    }

    return circular;
  }

  /**
   * Validate dependencies and create validation result
   */
  validateDependencies(
    dependencyGraph: TokenDependencyGraph,
    circularDependencies: Set<string>,
  ): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    // Check circular dependencies
    if (circularDependencies.size > 0) {
      const circularPaths = Array.from(circularDependencies).join(", ");
      errors.push({
        path: "tokens",
        message: `Circular dependencies detected: ${circularPaths}`,
        code: "CIRCULAR_DEPENDENCIES",
        severity: "error",
      });
    }

    // Check for missing references
    for (const [path, tokenRef] of dependencyGraph.nodes) {
      if (
        !tokenRef.resolved &&
        this.isTokenReference(tokenRef.value as string)
      ) {
        const refPath = this.extractReferencePath(tokenRef.value as string);
        const fullRefPath = refPath.startsWith("tokens.")
          ? refPath
          : `tokens.${refPath}`;
        if (!dependencyGraph.nodes.has(fullRefPath)) {
          errors.push({
            path,
            message: `Token reference not found: ${fullRefPath}`,
            code: "MISSING_TOKEN_REFERENCE",
            severity: "error",
          });
        }
      }
    }

    // Check for deep nesting
    for (const [path, tokenRef] of dependencyGraph.nodes) {
      const depth = this.calculateReferenceDepth(path, dependencyGraph);
      if (depth > this.maxDepth) {
        warnings.push({
          path,
          message: `Token reference depth (${depth}) exceeds recommended maximum (${this.maxDepth})`,
          code: "DEEP_REFERENCE_NESTING",
          severity: "warning",
        });
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Resolve token values by following references
   */
  resolveTokenValues(
    tokenSet: TokenSet,
    dependencyGraph: TokenDependencyGraph,
  ): TokenSet {
    const resolvedTokenSet = JSON.parse(JSON.stringify(tokenSet)); // Deep clone

    const resolveValue = (
      value: TokenValue,
      path: string,
      resolved: Map<string, TokenValue>,
      visited: Set<string>,
      depth: number = 0,
    ): TokenValue => {
      if (depth > this.maxDepth) {
        throw new Error(`Maximum reference depth exceeded for token: ${path}`);
      }

      // If not a reference, return as-is
      if (!this.isTokenReference(value)) {
        return value;
      }

      const refPath = this.extractReferencePath(value as string);
      const fullRefPath = refPath.startsWith("tokens.")
        ? refPath
        : `tokens.${refPath}`;

      // Check for circular reference
      if (visited.has(fullRefPath)) {
        // Return the original reference for circular dependencies
        return value;
      }

      visited.add(fullRefPath);

      // Check if already resolved
      if (resolved.has(fullRefPath)) {
        return resolved.get(fullRefPath)!;
      }

      // Get referenced token
      const refToken = dependencyGraph.nodes.get(fullRefPath);
      if (!refToken) {
        throw new Error(`Token reference not found: ${fullRefPath}`);
      }

      // Resolve the referenced token's value
      let resolvedValue: TokenValue;
      if (
        refToken.resolved &&
        refToken.value !== undefined &&
        !this.isTokenReference(refToken.value)
      ) {
        resolvedValue = refToken.value;
      } else if (
        refToken.value !== undefined &&
        this.isTokenReference(refToken.value)
      ) {
        // Recursively resolve reference
        resolvedValue = resolveValue(
          refToken.value,
          fullRefPath,
          resolved,
          visited,
          depth + 1,
        );
      } else if (!refToken.resolved && refToken.value !== undefined) {
        // Token has a value but is marked as unresolved (shouldn't happen with current logic)
        resolvedValue = refToken.value;
      } else {
        // This shouldn't happen if the graph is built correctly
        throw new Error(`Unresolved token reference: ${fullRefPath}`);
      }

      // Cache the resolved value
      resolved.set(fullRefPath, resolvedValue);
      return resolvedValue;
    };

    const resolveToken = (
      token: any,
      path: string,
      resolved: Map<string, TokenValue>,
      visited: Set<string>,
    ): any => {
      if (this.isTokenGroup(token)) {
        const resolvedGroup = { ...token };

        if (token.children) {
          resolvedGroup.children = {};
          Object.entries(token.children).forEach(([key, value]) => {
            const childPath = path ? `${path}.${key}` : key;
            resolvedGroup.children[key] = resolveToken(
              value,
              childPath,
              resolved,
              visited,
            );
          });
        }

        return resolvedGroup;
      } else if (token && typeof token === "object" && "$type" in token) {
        const resolvedToken = { ...token };

        try {
          const originalValue = token.$value;
          const resolvedValue = resolveValue(
            token.$value,
            path,
            resolved,
            visited,
          );
          resolvedToken.$value = resolvedValue;
        } catch (error) {
          // If resolution fails, keep original value but add warning
          console.warn(`Failed to resolve token ${path}:`, error);
          // In a real implementation, you might want to add this to validation results
        }

        return resolvedToken;
      }

      return token;
    };

    const resolved = new Map<string, TokenValue>();

    if (resolvedTokenSet.tokens) {
      // Process each token individually
      Object.entries(resolvedTokenSet.tokens).forEach(([key, token]) => {
        const tokenPath = `tokens.${key}`;
        resolvedTokenSet.tokens[key] = resolveToken(
          token,
          tokenPath,
          resolved,
          new Set<string>(),
        );
      });
    }

    return resolvedTokenSet;
  }

  /**
   * Apply transformation pipeline to resolved tokens
   */
  applyTransforms(tokenSet: TokenSet): TokenSet {
    if (this.transforms.length === 0) {
      return tokenSet;
    }

    const transformedTokenSet = JSON.parse(JSON.stringify(tokenSet)); // Deep clone

    const applyTransformsToToken = (token: any, path: string): any => {
      if (this.isTokenGroup(token)) {
        const transformedGroup = { ...token };

        if (token.children) {
          transformedGroup.children = {};
          Object.entries(token.children).forEach(([key, value]) => {
            const childPath = path ? `${path}.${key}` : key;
            transformedGroup.children[key] = applyTransformsToToken(
              value,
              childPath,
            );
          });
        }

        return transformedGroup;
      } else if (token && typeof token === "object" && "$type" in token) {
        const transformedToken = { ...token };

        // Apply applicable transforms
        for (const transform of this.transforms) {
          if (transform.type === token.$type) {
            const context: TransformContext = {
              token: transformedToken,
              path,
              tokens: transformedTokenSet,
              options: {}, // Default empty options
            };

            try {
              transformedToken.$value = transform.transformer(
                transformedToken.$value,
                context,
              );
            } catch (error) {
              console.warn(
                `Transform ${transform.name} failed for token ${path}:`,
                error,
              );
              // Keep original value if transform fails
            }
          }
        }

        return transformedToken;
      }

      return token;
    };

    if (transformedTokenSet.tokens) {
      // Process each token individually
      Object.entries(transformedTokenSet.tokens).forEach(([key, token]) => {
        const tokenPath = `tokens.${key}`;
        transformedTokenSet.tokens[key] = applyTransformsToToken(
          token,
          tokenPath,
        );
      });
    }

    return transformedTokenSet;
  }

  /**
   * Calculate resolution statistics
   */
  private calculateStatistics(
    dependencyGraph: TokenDependencyGraph,
    circularDependencies: Set<string>,
  ) {
    const totalTokens = dependencyGraph.nodes.size;
    let resolvedReferences = 0;
    let maxDepth = 0;

    for (const [path, tokenRef] of dependencyGraph.nodes) {
      if (!tokenRef.resolved) {
        resolvedReferences++;
      }

      const depth = this.calculateReferenceDepth(path, dependencyGraph);
      maxDepth = Math.max(maxDepth, depth);
    }

    return {
      totalTokens,
      resolvedReferences,
      circularDependencies: circularDependencies.size,
      maxDepth,
    };
  }

  /**
   * Calculate reference depth for a token
   */
  private calculateReferenceDepth(
    path: string,
    dependencyGraph: TokenDependencyGraph,
  ): number {
    const visited = new Set<string>();

    const calculateDepth = (currentPath: string, depth: number = 0): number => {
      if (visited.has(currentPath)) {
        return depth; // Circular reference detected
      }

      visited.add(currentPath);

      const dependencies = dependencyGraph.edges.get(currentPath);
      if (!dependencies || dependencies.size === 0) {
        return depth;
      }

      let maxChildDepth = depth;
      for (const dep of dependencies) {
        maxChildDepth = Math.max(maxChildDepth, calculateDepth(dep, depth + 1));
      }

      return maxChildDepth;
    };

    return calculateDepth(path);
  }

  /**
   * Check if a value is a token reference
   */
  private isTokenReference(value: TokenValue): boolean {
    return (
      typeof value === "string" && value.startsWith("{") && value.endsWith("}")
    );
  }

  /**
   * Extract reference path from token reference string
   */
  private extractReferencePath(reference: string): string {
    return reference.slice(1, -1); // Remove { and }
  }

  /**
   * Check if a token is a token group
   */
  private isTokenGroup(token: DesignToken | TokenGroup | any): boolean {
    return (
      token &&
      typeof token === "object" &&
      (!token.$type || token.$type === "group")
    );
  }
}

/**
 * Built-in token transforms
 */
export class TokenTransforms {
  /**
   * Transform color values to ensure consistent format
   */
  static colorNormalizer: TokenTransform = {
    name: "color-normalizer",
    type: "color",
    transformer: (value: TokenValue, context: TransformContext): TokenValue => {
      if (
        typeof value === "object" &&
        value !== null &&
        "colorSpace" in value
      ) {
        const colorValue = value as any;

        // Ensure channels are normalized to 0-1 range
        if (Array.isArray(colorValue.channels)) {
          colorValue.channels = colorValue.channels.map((channel: number) => {
            return Math.max(0, Math.min(1, channel));
          });
        }

        // Ensure alpha is valid
        if (colorValue.alpha !== undefined) {
          colorValue.alpha = Math.max(0, Math.min(1, colorValue.alpha));
        }

        // Add a flag to indicate transformation was applied
        (colorValue as any).transformed = true;

        return colorValue;
      }

      return value;
    },
  };

  /**
   * Transform dimension values to ensure consistent units
   */
  static dimensionNormalizer: TokenTransform = {
    name: "dimension-normalizer",
    type: "dimension",
    transformer: (value: TokenValue, context: TransformContext): TokenValue => {
      if (typeof value === "object" && value !== null && "unit" in value) {
        const dimensionValue = value as any;

        // Ensure value is a number
        if (typeof dimensionValue.value !== "number") {
          dimensionValue.value = parseFloat(dimensionValue.value) || 0;
        }

        return dimensionValue;
      }

      return value;
    },
  };

  /**
   * Transform font values to ensure consistent structure
   */
  static fontNormalizer: TokenTransform = {
    name: "font-normalizer",
    type: "font",
    transformer: (value: TokenValue, context: TransformContext): TokenValue => {
      if (
        typeof value === "object" &&
        value !== null &&
        "fontFamily" in value
      ) {
        const fontValue = value as any;

        // Ensure fontFamily is an array
        if (typeof fontValue.fontFamily === "string") {
          fontValue.fontFamily = [fontValue.fontFamily];
        }

        return fontValue;
      }

      return value;
    },
  };

  /**
   * Get all built-in transforms
   */
  static getAll(): TokenTransform[] {
    return [
      TokenTransforms.colorNormalizer,
      TokenTransforms.dimensionNormalizer,
      TokenTransforms.fontNormalizer,
    ];
  }
}

// Export default resolver instance
export const tokenDependencyResolver = new TokenDependencyResolver({
  maxDepth: 50,
  throwOnCircular: false,
  resolveNested: true,
  transforms: TokenTransforms.getAll(),
});
